import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker, Popover, Select, Spin } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import {
    EMPTY_STORY_MAP_BOARD_FILTERS,
    hasActiveStoryMapBoardFilters,
} from '../../../storyMap.service'
import { STORY_STATUS_OPTIONS } from '../utils'
import AutoHideTooltip from './AutoHideTooltip'
import styles from './StoryMapBoardFilters.module.css'

const COMMENT_FILTER_OPTIONS = [
    { value: 'all', label: 'Any time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This week' },
    { value: 'custom', label: 'Custom range' },
]

const CONTENT_FILTER_CHIPS = [
    { key: 'hasComments', label: 'Comments', icon: 'mdi:comment-text-outline' },
    { key: 'hasAttachment', label: 'Attachments', icon: 'mdi:paperclip' },
    { key: 'hasDescription', label: 'Description', icon: 'mdi:text-box-outline' },
]

const STATUS_API_TO_KEY = {
    'NOT READY': 'not_ready',
    'IN PROGRESS': 'in_progress',
    DONE: 'done',
}

const getStatusTagClass = (apiValue) => {
    const key = STATUS_API_TO_KEY[apiValue]
    return key ? styles[`statusTag_${key}`] : ''
}

const countAdvancedFilters = (filters = EMPTY_STORY_MAP_BOARD_FILTERS) => {
    const f = filters || EMPTY_STORY_MAP_BOARD_FILTERS
    let count = 0

    const hasPresetCommentFilter =
        f.commentFilter &&
        f.commentFilter !== 'all' &&
        f.commentFilter !== 'custom'
    const hasCustomCommentRange = Boolean(f.from && f.to)

    if (hasPresetCommentFilter || hasCustomCommentRange || f.commentFilter === 'custom') {
        count += 1
    }
    if (Array.isArray(f.storyStatus) && f.storyStatus.length > 0) {
        count += 1
    }
    if (f.hasComments || f.hasAttachment || f.hasDescription) {
        count += 1
    }

    return count
}

const StoryMapBoardFilters = ({
    filters,
    onChange,
    loading = false,
    isFilterActive = false,
    filterMatchCount = 0,
    filterMatchIndex = 0,
    onPrevMatch,
    onNextMatch,
    variant = 'bar',
}) => {
    const [filtersOpen, setFiltersOpen] = useState(false)
    const filtersRef = useRef(filters)
    const isTabBar = variant === 'tabBar'

    useEffect(() => {
        filtersRef.current = filters
    }, [filters])

    const emitChange = useCallback(
        (patch) => {
            onChange?.({
                ...EMPTY_STORY_MAP_BOARD_FILTERS,
                ...filtersRef.current,
                ...patch,
            })
        },
        [onChange]
    )

    const statusOptions = useMemo(
        () =>
            STORY_STATUS_OPTIONS.map((option) => ({
                value: option.apiValue,
                label: option.label,
            })),
        []
    )

    const commentFilter = filters?.commentFilter || 'all'
    const showCustomRange = commentFilter === 'custom'
    const dateRangeValue =
        filters?.from && filters?.to
            ? [dayjs(filters.from), dayjs(filters.to)]
            : null

    const hasActive = hasActiveStoryMapBoardFilters(filters)
    const advancedFilterCount = countAdvancedFilters(filters)

    const filtersTooltip =
        advancedFilterCount > 0
            ? `${advancedFilterCount} filter${
                  advancedFilterCount > 1 ? 's' : ''
              } applied — click to edit`
            : 'Filter by comments, status, and card content'

    const clearTooltip = hasActive
        ? 'Clear all filters and search'
        : 'No active filters'

    const handleCommentFilterChange = (value) => {
        if (value === 'custom') {
            emitChange({ commentFilter: 'custom' })
            requestAnimationFrame(() => {
                document.activeElement?.blur?.()
            })
            return
        }

        emitChange({
            commentFilter: value,
            from: null,
            to: null,
        })
    }

    const handleDateRangeChange = (dates) => {
        if (!dates?.[0] || !dates?.[1]) {
            emitChange({ from: null, to: null, commentFilter: 'custom' })
            return
        }

        emitChange({
            commentFilter: 'custom',
            from: dates[0].startOf('day').toISOString(),
            to: dates[1].endOf('day').toISOString(),
        })
    }

    const disableFutureDates = (current) =>
        Boolean(current && current.isAfter(dayjs().endOf('day')))

    const handleContentChipToggle = (key) => {
        emitChange({ [key]: !filters?.[key] })
    }

    const handleClear = () => {
        setFiltersOpen(false)
        onChange?.({ ...EMPTY_STORY_MAP_BOARD_FILTERS })
    }

    const renderStatusTag = ({ label, value, closable, onClose }) => (
        <span
            className={`${styles.statusTag} ${getStatusTagClass(value)}`}
            onMouseDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
            }}
        >
            <span className={styles.statusTagLabel}>{label}</span>
            {closable ? (
                <button
                    type="button"
                    className={styles.statusTagClose}
                    onClick={onClose}
                    aria-label={`Remove ${label}`}
                >
                    <Icon icon="mdi:close" width={12} />
                </button>
            ) : null}
        </span>
    )

    const advancedFiltersPanel = (
        <div className={styles.popoverPanel}>
            <div className={styles.popoverSection}>
                <span className={styles.popoverLabel}>Comments</span>
                <div className={styles.popoverFieldStack}>
                    <Select
                        className={styles.popoverSelect}
                        size="small"
                        value={commentFilter}
                        options={COMMENT_FILTER_OPTIONS}
                        onChange={handleCommentFilterChange}
                        popupMatchSelectWidth={false}
                        autoFocus={false}
                    />
                    {showCustomRange ? (
                        <DatePicker.RangePicker
                            className={styles.popoverDateRange}
                            size="small"
                            value={dateRangeValue}
                            onChange={handleDateRangeChange}
                            disabledDate={disableFutureDates}
                            allowEmpty={[false, false]}
                            placeholder={['From', 'To']}
                            format="MMM D, YYYY"
                            autoFocus={false}
                        />
                    ) : null}
                </div>
            </div>

            <div className={styles.popoverDivider} aria-hidden />

            <div className={styles.popoverSection}>
                <span className={styles.popoverLabel}>Status</span>
                <Select
                    className={styles.popoverSelectStatus}
                    size="small"
                    mode="multiple"
                    allowClear
                    showSearch={false}
                    placeholder="All statuses"
                    value={filters?.storyStatus ?? []}
                    options={statusOptions}
                    onChange={(value) => emitChange({ storyStatus: value })}
                    tagRender={renderStatusTag}
                    maxTagCount={2}
                    maxTagPlaceholder={(omitted) => `+${omitted.length}`}
                    popupMatchSelectWidth={false}
                    autoFocus={false}
                />
            </div>

            <div className={styles.popoverDivider} aria-hidden />

            <div className={styles.popoverSection}>
                <span className={styles.popoverLabel}>Card content</span>
                <div
                    className={styles.chipGroup}
                    role="group"
                    aria-label="Card content filters"
                >
                    {CONTENT_FILTER_CHIPS.map((chip) => {
                        const isActive = Boolean(filters?.[chip.key])

                        return (
                            <button
                                key={chip.key}
                                type="button"
                                className={`${styles.filterChip} ${
                                    isActive ? styles.filterChipActive : ''
                                }`}
                                aria-pressed={isActive}
                                aria-label={chip.label}
                                onClick={() => handleContentChipToggle(chip.key)}
                            >
                                <Icon icon={chip.icon} width={15} aria-hidden />
                                <span className={styles.filterChipLabel}>
                                    {chip.label}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )

    return (
        <div
            className={`${isTabBar ? styles.filterBarTabBar : styles.filterBar}`}
        >
            <div
                className={
                    isTabBar ? styles.filterBarTabBarMain : styles.filterBarMain
                }
            >
                <Popover
                    content={advancedFiltersPanel}
                    trigger="click"
                    placement="bottomRight"
                    open={filtersOpen}
                    onOpenChange={setFiltersOpen}
                    overlayClassName={styles.filtersPopover}
                    arrow={false}
                >
                    <span className={styles.filtersBtnWrap}>
                        <AutoHideTooltip title={filtersTooltip} placement="bottom">
                            <button
                                type="button"
                                className={`${styles.filtersBtn} ${
                                    advancedFilterCount > 0
                                        ? styles.filtersBtnActive
                                        : ''
                                } ${filtersOpen ? styles.filtersBtnOpen : ''}`}
                                aria-expanded={filtersOpen}
                            >
                                <Icon icon="mdi:filter-variant" width={16} />
                                <span>Filters</span>
                                {advancedFilterCount > 0 ? (
                                    <span className={styles.filtersBadge}>
                                        {advancedFilterCount}
                                    </span>
                                ) : null}
                                <Icon
                                    icon="mdi:chevron-down"
                                    width={16}
                                    className={styles.filtersChevron}
                                />
                            </button>
                        </AutoHideTooltip>
                    </span>
                </Popover>
            </div>

            <div className={styles.filterBarActions}>
                {isFilterActive ? (
                    <div className={styles.resultNav}>
                        <span className={styles.resultCount}>
                            {filterMatchCount > 0
                                ? `${filterMatchIndex + 1} of ${filterMatchCount}`
                                : '0 results'}
                        </span>
                        <div className={styles.resultArrows}>
                            <AutoHideTooltip
                                title="Previous result"
                                placement="bottom"
                            >
                                <button
                                    type="button"
                                    className={styles.resultArrowBtn}
                                    onClick={onPrevMatch}
                                    disabled={filterMatchCount <= 1 || loading}
                                    aria-label="Previous result"
                                >
                                    <Icon icon="mdi:chevron-up" width={18} />
                                </button>
                            </AutoHideTooltip>
                            <AutoHideTooltip
                                title="Next result"
                                placement="bottom"
                            >
                                <button
                                    type="button"
                                    className={styles.resultArrowBtn}
                                    onClick={onNextMatch}
                                    disabled={filterMatchCount <= 1 || loading}
                                    aria-label="Next result"
                                >
                                    <Icon icon="mdi:chevron-down" width={18} />
                                </button>
                            </AutoHideTooltip>
                        </div>
                    </div>
                ) : null}

                <AutoHideTooltip title={clearTooltip} placement="bottom">
                    <span className={styles.clearBtnWrap}>
                        <button
                            type="button"
                            className={styles.clearBtn}
                            onClick={handleClear}
                            disabled={!hasActive || loading}
                            aria-label="Clear filters"
                        >
                        {loading ? (
                            <Spin size="small" />
                        ) : (
                            <Icon icon="mdi:filter-off-outline" width={16} />
                        )}
                        </button>
                    </span>
                </AutoHideTooltip>
            </div>
        </div>
    )
}

export default StoryMapBoardFilters
